import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const formatCurrency = (amount, currency = "PHP") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: 2,
  }).format(amount);
};

export const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatTime = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const formatDuration = (minutes) => {
  if (!minutes) return "0:00";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours}:${mins.toString().padStart(2, "0")}`;
};

export const formatDurationLong = (minutes) => {
  if (!minutes) return "0h 0m";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${mins}m`;
};

export const PAYMENT_STATUS_LABELS = {
  paid: "Paid",
  planned: "Planned",
  pending: "Pending",
  partially_paid: "Partially Paid",
  overdue: "Overdue",
};

export const getPaymentStatusColor = (status) => {
  const colors = {
    paid: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
    planned: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
    pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300",
    partially_paid: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
    overdue: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  };
  return colors[status?.toLowerCase()] || colors.planned;
};

export const getDueDateUrgencyColor = (dueDate) => {
  if (!dueDate) return "";
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Append time only if dueDate is a plain YYYY-MM-DD string to avoid timezone shifts
  const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(dueDate) ? dueDate + "T00:00:00" : dueDate;
  const due = new Date(dateStr);
  if (isNaN(due.getTime())) return "";
  const delta = Math.round((due - today) / 86400000);
  if (delta < 0) return "text-red-600 dark:text-red-400";
  if (delta === 0) return "text-orange-600 dark:text-orange-400";
  if (delta <= 3) return "text-blue-600 dark:text-blue-400";
  return "";
};

export const getStatusColor = (status) => {
  const colors = {
    new: "status-draft",
    paid: "status-paid",
    sent: "status-sent",
    pending: "status-pending",
    overdue: "status-overdue",
    draft: "status-draft",
    active: "status-active",
    completed: "status-completed",
    in_progress: "status-active",
    on_hold: "status-pending",
    approved: "status-paid",
    rejected: "status-overdue",
    applied: "status-pending",
    interviewing: "status-active",
    offered: "status-paid",
    declined: "status-overdue",
  };
  return colors[status?.toLowerCase()] || "status-draft";
};

export const getPriorityColor = (priority) => {
  const colors = {
    high: "priority-high",
    medium: "priority-medium",
    low: "priority-low",
  };
  return colors[priority?.toLowerCase()] || "";
};

export const isWithinShift = (timeString, shiftStart, shiftEnd) => {
  if (!shiftStart || !shiftEnd || !timeString) return null;
  
  const time = new Date(timeString);
  const [startHour, startMin] = shiftStart.split(":").map(Number);
  const [endHour, endMin] = shiftEnd.split(":").map(Number);
  
  const hour = time.getHours();
  const min = time.getMinutes();
  
  const timeValue = hour * 60 + min;
  const startValue = startHour * 60 + startMin;
  const endValue = endHour * 60 + endMin;
  
  return timeValue >= startValue && timeValue <= endValue;
};

export const CURRENCIES = [
  { code: "PHP", symbol: "₱", name: "Philippine Peso" },
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "IDR", symbol: "Rp", name: "Indonesian Rupiah" },
  { code: "JPY", symbol: "¥", name: "Japanese Yen" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
  { code: "CAD", symbol: "C$", name: "Canadian Dollar" },
  { code: "SGD", symbol: "S$", name: "Singapore Dollar" },
];

export const TASK_STATUSES = [
  { value: "new", label: "NEW" },
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "on_hold", label: "On Hold" },
];

export const TASK_PRIORITIES = [
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

export const PROJECT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "on_hold", label: "On Hold" },
  { value: "cancelled", label: "Cancelled" },
];

export const CLIENT_STATUSES = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export const INVOICE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "paid", label: "Paid" },
  { value: "partial", label: "Partial" },
  { value: "overdue", label: "Overdue" },
];

export const QUOTE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const LEAD_STATUSES = [
  { value: "applied", label: "Applied" },
  { value: "interviewing", label: "Interviewing" },
  { value: "offered", label: "Offered" },
  { value: "accepted", label: "Accepted" },
  { value: "declined", label: "Declined" },
  { value: "rejected", label: "Rejected" },
];

export const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "America/New_York (EST/EDT)" },
  { value: "America/Chicago", label: "America/Chicago (CST/CDT)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (PST/PDT)" },
  { value: "Europe/London", label: "Europe/London (GMT/BST)" },
  { value: "Europe/Paris", label: "Europe/Paris (CET/CEST)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (JST)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (CST)" },
  { value: "Asia/Hong_Kong", label: "Asia/Hong_Kong (HKT)" },
  { value: "Asia/Manila", label: "Asia/Manila (PHT)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (AEDT/AEST)" },
  { value: "Pacific/Auckland", label: "Pacific/Auckland (NZDT/NZST)" },
];

export const convertTimeToTimezone = (timeString, fromTimezone, toTimezone) => {
  if (!timeString) return "";
  
  try {
    const [hours, minutes] = timeString.split(":").map(Number);
    
    // Create a reference date to calculate timezone offset
    const referenceDate = new Date("2024-01-15T12:00:00Z");
    
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: fromTimezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    
    const formatterTarget = new Intl.DateTimeFormat("en-US", {
      timeZone: toTimezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    
    // Get offset for source timezone
    const sourceTime = formatter.format(referenceDate);
    const [sourceHours] = sourceTime.split(":").map(Number);
    
    // Get offset for target timezone
    const targetTime = formatterTarget.format(referenceDate);
    const [targetHours] = targetTime.split(":").map(Number);
    
    // Calculate offset difference
    const offsetDiff = sourceHours - targetHours;
    
    // Apply offset to our time
    let convertedHours = hours + offsetDiff;
    let convertedMinutes = minutes;
    
    // Handle day wrap-around
    if (convertedHours < 0) {
      convertedHours += 24;
    } else if (convertedHours >= 24) {
      convertedHours -= 24;
    }
    
    return `${String(convertedHours).padStart(2, "0")}:${String(convertedMinutes).padStart(2, "0")}`;
  } catch (e) {
    console.error("Timezone conversion error:", e);
    return timeString;
  }
};
